import { useId, useState } from 'react';
import {
    FaceSmileIcon,
    PaperClipIcon,
    LinkIcon,
} from '@heroicons/react/24/outline';
import './TextareaModern.css';

/**
 * @param {object} props
 * @param {string} [props.id]
 * @param {string} [props.name]
 * @param {string} [props.label]
 * @param {string} [props.value]
 * @param {(event: import('react').ChangeEvent<HTMLTextAreaElement>) => void} props.onChange
 * @param {string} [props.placeholder]
 * @param {boolean} [props.required]
 * @param {string|null} [props.error]
 * @param {boolean} [props.disabled]
 * @param {number} [props.maxLength]
 * @param {number} [props.minRows]
 * @param {boolean} [props.showEmoji]
 * @param {boolean} [props.showAttach]
 * @param {boolean} [props.showLink]
 * @param {() => void} [props.onEmojiClick]
 * @param {() => void} [props.onAttachClick]
 * @param {() => void} [props.onLinkClick]
 * @param {string} [props.className]
 */
export function TextareaModern({
    id: idProp,
    name,
    label,
    value = '',
    onChange,
    placeholder,
    required = false,
    error = null,
    disabled = false,
    maxLength = 1000,
    minRows = 4,
    showEmoji = false,
    showAttach = false,
    showLink = false,
    onEmojiClick,
    onAttachClick,
    onLinkClick,
    className = '',
}) {
    const generatedId = useId();
    const id = idProp || `textarea-modern-${generatedId}`;
    const [focused, setFocused] = useState(false);
    const length = String(value || '').length;
    const overLimit = maxLength > 0 && length > maxLength;
    const atLimit = maxLength > 0 && length === maxLength;
    const nearLimit = maxLength > 0 && !overLimit && !atLimit && length >= Math.floor(maxLength * 0.9);
    const showToolbar = showEmoji || showAttach || showLink;
    const displayError = error || (overLimit
        ? `El texto no puede superar ${maxLength.toLocaleString('es-MX')} caracteres.`
        : null);

    const handleChange = (event) => {
        if (!onChange) return;

        const raw = event.target.value;
        if (maxLength > 0 && raw.length > maxLength) {
            const truncated = raw.slice(0, maxLength);
            onChange({
                ...event,
                target: {
                    ...event.target,
                    value: truncated,
                },
            });
            return;
        }

        onChange(event);
    };

    return (
        <div className={`textarea-modern ${className}`.trim()}>
            {label ? (
                <label htmlFor={id} className="textarea-modern__label">
                    {label}
                    {required ? (
                        <span className="textarea-modern__required" aria-hidden="true">*</span>
                    ) : null}
                </label>
            ) : null}

            <div
                className={[
                    'textarea-modern__shell',
                    focused ? 'is-focused' : '',
                    displayError ? 'has-error' : '',
                ].filter(Boolean).join(' ')}
            >
                <div className="textarea-modern__inner">
                    <textarea
                        id={id}
                        name={name}
                        value={value}
                        onChange={handleChange}
                        onFocus={() => setFocused(true)}
                        onBlur={() => setFocused(false)}
                        placeholder={placeholder}
                        disabled={disabled}
                        rows={minRows}
                        maxLength={maxLength > 0 ? maxLength : undefined}
                        aria-invalid={displayError ? true : undefined}
                        aria-describedby={displayError ? `${id}-error` : undefined}
                        className="textarea-modern__input"
                    />

                    <div className="textarea-modern__footer">
                        {showToolbar ? (
                            <div className="textarea-modern__tools">
                                {showEmoji ? (
                                    <button
                                        type="button"
                                        className="textarea-modern__tool"
                                        onClick={onEmojiClick}
                                        disabled={disabled}
                                        aria-label="Insertar emoji"
                                    >
                                        <FaceSmileIcon className="textarea-modern__tool-icon" aria-hidden="true" />
                                    </button>
                                ) : null}
                                {showAttach ? (
                                    <button
                                        type="button"
                                        className="textarea-modern__tool"
                                        onClick={onAttachClick}
                                        disabled={disabled}
                                        aria-label="Adjuntar archivo"
                                    >
                                        <PaperClipIcon className="textarea-modern__tool-icon" aria-hidden="true" />
                                    </button>
                                ) : null}
                                {showLink ? (
                                    <button
                                        type="button"
                                        className="textarea-modern__tool"
                                        onClick={onLinkClick}
                                        disabled={disabled}
                                        aria-label="Insertar enlace"
                                    >
                                        <LinkIcon className="textarea-modern__tool-icon" aria-hidden="true" />
                                    </button>
                                ) : null}
                            </div>
                        ) : null}

                        {maxLength > 0 ? (
                            <span
                                className={[
                                    'textarea-modern__counter',
                                    overLimit ? 'is-over' : '',
                                    (atLimit || nearLimit) ? 'is-warn' : '',
                                ].filter(Boolean).join(' ')}
                                aria-live="polite"
                            >
                                {length}
                                /
                                {maxLength}
                            </span>
                        ) : null}
                    </div>
                </div>
            </div>

            {displayError ? (
                <p id={`${id}-error`} className="textarea-modern__error" role="alert">
                    {displayError}
                </p>
            ) : null}
        </div>
    );
}
